// src/services/socket.ts
let socket: WebSocket | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelayMs = 1000;
const MAX_DELAY = 15000;

const envWS =
  (process.env as any)?.REACT_APP_WS_URL || "";

const getCookie = (name: string) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : "";
};

const resolveWebSocketURL = (token: string) => {
  if (envWS) return token ? `${envWS}?token=${encodeURIComponent(token)}` : envWS;
  const isHttps = location.protocol === "https:";
  const scheme = isHttps ? "wss" : "ws";
  const isLocal = location.hostname.endsWith("aibex-local.com");
  const host = isLocal ? "api.aibex-local.com" : "api.aibex.com";
  const base = `${scheme}://${host}/ws`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

const getToken = () => {
  return (
    localStorage.getItem("access_token") ||
    getCookie("access_token") ||
    getCookie("session") ||
    ""
  );
};

const shouldRetry = (code: number) => {
  // 認証不備/手動系はリトライしない
  if (code === 1008 /* Policy Violation (unauthorized) */) return false;
  if (code >= 4000 && code < 5000) return false; // 4000台（手動クローズ等）
  return true;
};

const connect = () => {
  const token = getToken();
  if (!token) {
    return;
  }

  const url = resolveWebSocketURL(token);
  const protocols = ["access_token", token] as string[];

  if (socket && socket.readyState === WebSocket.OPEN) return;

  socket = new WebSocket(url, protocols);

  const hsTimeout = setTimeout(() => {
    if (socket && socket.readyState === WebSocket.CONNECTING) {
      console.warn("⏱️ WS handshake timeout → abort (no retry until next trigger)");
      try { socket.close(4000, "handshake-timeout"); } catch {}
    }
  }, 10000);

  socket.onopen = () => {
    clearTimeout(hsTimeout);
    console.log("✅ WebSocket connected");
    retryDelayMs = 1000;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  };

  socket.onmessage = (ev) => console.log("📩", ev.data);

  socket.onclose = (ev) => {
    clearTimeout(hsTimeout);
    console.warn("⚠️ WebSocket closed:", ev.code, ev.reason || "");
    if (shouldRetry(ev.code)) {
      if (!retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          connect();
          retryDelayMs = Math.min(retryDelayMs * 2, MAX_DELAY);
        }, retryDelayMs);
      }
    } else {
      console.info("WS: no retry policy for this close code.");
    }
  };

  socket.onerror = (err) => {
    console.error("❌ WebSocket error:", err);
  };
};

// 公開API
export const initWebSocket = () => {
  // ここでは「トークンがある時だけ」接続
  if (!socket || socket.readyState === WebSocket.CLOSED) connect();
  return socket!;
};

export const sendMessage = (msg: string) => {
  if (socket?.readyState === WebSocket.OPEN) socket.send(msg);
  else console.error("🚫 WebSocket not connected. Cannot send:", msg);
};

export const reconnectWebSocket = () => {
  try { socket?.close(4100, "manual-reconnect"); } catch {}
  socket = null;
  retryDelayMs = 1000;
  connect();
};
